import React, { useState, useEffect, useCallback } from 'react';
import { DataGrid, GridToolbarContainer } from '@mui/x-data-grid';
import {
  Box,
  Stack,
  Paper,
  InputBase,
  IconButton,
  LinearProgress
} from '@mui/material';
import MyButton from './MyButton';
import { IconPlus, IconPencil, IconTrash, IconSearch } from '@tabler/icons';
import ReferralTypeOptions from '../../referral/tables/ReferralTypeOptions';
import AppointmentMaintainedOptions from '../../referral/tables/AppointmentMaintainedOptions';
import ReferralCreateButton from '../../referral/ReferralCreate/ReferralCreateButton';

const PAGE_SIZE_OPTIONS = [15, 25, 50];

function CustomizedInputBase({ onChange, onChangeDropdown, addReferral, fromReceived }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap'
      }}
    >
      <Paper
        component="form"
        elevation={1}
        sx={{ p: '2px 4px', display: 'flex', alignItems: 'center', width: 400 }}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {/* {
                (onChangeDropdown && typeof onChangeDropdown === 'function') &&
                <FormControl style={{width: '130px'}}>
                    <InputLabel id="demo-simple-select-label">Search By</InputLabel>
                    <Select
                        labelId="demo-simple-select-label"
                        id="demo-simple-select"
                        defaultValue={'Name'}
                        label="Search"
                        onChange={onChangeDropdown}
                    >
                        <MenuItem value={'Name'}>Name</MenuItem>
                        <MenuItem value={'Birthdate'}>Birthdate</MenuItem>
                    </Select>
                </FormControl>
            } */}

        <InputBase
          sx={{ ml: 1, flex: 1 }}
          onChange={onChange}
          placeholder={(onChangeDropdown && typeof onChangeDropdown === 'function') ? "Search by Name" : "Search..."}
          inputProps={{ 'aria-label': 'search' }}
        />
        <IconButton type="button" sx={{ p: '10px' }} aria-label="search">
          <IconSearch />
        </IconButton>
      </Paper>

      {addReferral && <ReferralCreateButton fromReceived={fromReceived} />}
    </Box>
  );
}

function EditToolbar(props) {
  const { onDelete, onEdit } = props;

  return (
    <Stack direction="row"
      justifyContent="flex-end"
      alignItems="center"
      spacing={1}
      sx={{ width: '100%' }}>
      {
        (onDelete && typeof onDelete === 'function') &&
        <MyButton color="error"
          variant="contained"
          startIcon={<IconTrash />}
          onClick={onDelete}
          fullWidth={false}>
          Delete
        </MyButton>
      }
      {
        (onEdit && typeof onEdit === 'function') &&
        <MyButton variant="contained"
          startIcon={<IconPencil />}
          onClick={onEdit}
          fullWidth={false}>
          Edit
        </MyButton>
      }
    </Stack>
  );
}

function MainToolbar(props) {
  const { onAdd, addButtonText, onFilterChanged, onFilterFaxChanged, onAppointmentMaintainedFilterChanged, addReferral, fromReceived, onChangeDropdown } = props;
  const { borderRadius } = props;

  return (
    <Stack direction="row"
      justifyContent="space-between"
      alignItems="center"
      spacing={1}
      sx={{ width: '100%', borderRadius: borderRadius }}
      >
      {
        (onFilterChanged && typeof onFilterChanged === 'function') &&
        <CustomizedInputBase onChange={onFilterChanged} onChangeDropdown={onChangeDropdown} addReferral={addReferral} fromReceived={fromReceived}/>
      }

      {
        (onFilterFaxChanged && typeof onFilterFaxChanged === 'function') &&
        <ReferralTypeOptions onChange={onFilterFaxChanged} />
      }

      {
        (onAppointmentMaintainedFilterChanged && typeof onAppointmentMaintainedFilterChanged === 'function') &&
        <AppointmentMaintainedOptions onChange={onAppointmentMaintainedFilterChanged} />
      }

      {
        (onAdd && typeof onAdd === 'function') &&
        <MyButton color="primary"
          variant="contained"
          startIcon={<IconPlus />}
          onClick={onAdd}
          fullWidth={false}>
          {addButtonText}
        </MyButton>
      }
    </Stack>
  );
}

function TableToolbarContainer({ selectionMode, ...otherProps }) {
  const { borderRadius } = otherProps;

  return (
    <GridToolbarContainer
      sx={{
        bgcolor: selectionMode ? 'grey.500' : '#fff',
        width: '100%',
        p: 2,
        borderRadius: borderRadius
      }}>
      {
        selectionMode
          ?
          <EditToolbar {...otherProps} />
          :
          <MainToolbar {...otherProps} />
      }
    </GridToolbarContainer>
  );
}

export default function MyDataGrid({
  loading,
  data,
  columns,
  addReferral = false,
  fromReceived = false,
  onChangeDropdown = null,
  onAppointmentMaintainedFilterChanged = null,
  onFilterChanged = null,
  onFilterFaxChanged = null,
  onAdd = null,
  onDelete = null,
  onEdit = null,
  onSelectedRowsChanged = null,
  showToolbar = true,
  hideFooter = false,
  hideFooterPagination = false,
  minTableHeight = 400,
  onClick = null,
  height = '100%',
  addButtonText = 'New',
  initPage = 0,
  initPageSize = 25,
  onPageChange,
  onPageSizeChange,
  ...otherProps
}) {
  const [selectionModel, setSelectionModel] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [pageSize, setPageSize] = useState(initPageSize);
  const [page, setPage] = useState(initPage);

  const { rows, count } = data;

  useEffect(() => {
    if (onSelectedRowsChanged) {
      onSelectedRowsChanged(selectionModel);
      setSelectionMode(selectionModel.length > 0);
    }
  }, [selectionModel, onSelectedRowsChanged]);

  const _onEdit = useCallback(() => {
    onEdit(selectionModel)
  }, [onEdit, selectionModel]);

  const _onDelete = useCallback(() => {
    onDelete(selectionModel)
  }, [onDelete, selectionModel]);

  const { borderRadius } = otherProps;

  return (
    <Box style={{
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      height: height,
      minHeight: minTableHeight,
      width: '100%',
      borderRadius: borderRadius
    }}
    >
      <DataGrid

        sx={{
          '& .MuiDataGrid-cellContent': {
            padding: '16px 8px',
          }
        }}

        {...otherProps}

        // autoPageSize
        hideFooter={hideFooter}
        hideFooterPagination={hideFooterPagination}
        initialState={{
          pinnedColumns: { right: ['actions'] },
          columnMenu: {
            onAdd,
            onFilterChanged,
            onFilterFaxChanged,
            onAppointmentMaintainedFilterChanged,
            onDelete: onDelete ? _onDelete : onDelete,
            onEdit: onEdit ? _onEdit : onEdit,
          }
        }}
        checkboxSelection={false}
        disableSelectionOnClick

        showCellRightBorder={false}
        showCellLeftBorder={false}
        loading={loading}

        rowCount={count}
        rows={rows}
        columns={columns}
        onRowClick={onClick}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          onPageSizeChange(newPageSize);
        }}

        onPageChange={(newPage) => {
          setPage(newPage);
          if(onPageChange) onPageChange(newPage);
        }}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        pagination

        components={{
          LoadingOverlay: LinearProgress,
          Toolbar: showToolbar ? TableToolbarContainer : null,
          ColumnResizeIcon: () => null,
        }}
        componentsProps={{
          toolbar: {
            selectionMode: selectionMode,
            setSelectionMode: setSelectionMode,
            addReferral,
            fromReceived,
            onAdd,
            addButtonText,
            onChangeDropdown,
            onFilterChanged,
            onFilterFaxChanged,
            onAppointmentMaintainedFilterChanged
            // onDelete: onDelete ? _onDelete : onDelete,
            // onEdit: onEdit ? _onEdit : onEdit,
          },
        }}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        selectionModel={selectionModel}
        getRowHeight={() => 'auto'}
      />
    </Box>
  );
}